import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, MoreHorizontalIcon, PlusIcon, ChevronRightIcon, BuildingIcon, UserIcon, LandmarkIcon, SmartphoneIcon, CreditCardIcon, WalletIcon } from 'lucide-react';
import { mockSuppliers } from '../utils/mockSuppliers';
export function SupplierDetail() {
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const supplier = mockSuppliers.find(s => s.id === id) || mockSuppliers[0];
  const fullAddress = [supplier.addressLine1, supplier.addressLine2, supplier.city, supplier.province, supplier.postcode, supplier.country].filter(Boolean).join(', ');
  const billingEmail = supplier.useAsBillingEmail ? supplier.email : `${supplier.email} (separate billing)`;
  const bills = {
    total: supplier.amountOwed !== 'THB 0.00' ? 1 : 0,
    due: supplier.amountOwed !== 'THB 0.00' ? 1 : 0,
    awaitingApproval: 0,
    scheduled: 0,
    paid: 0
  };
  const history = [{
    id: 'h1',
    action: `${supplier.name} was created`,
    date: supplier.dateCreated.replace(',', ' at')
  }];
  return <div className="space-y-8 max-w-7xl mx-auto pb-12" data-id="element-11247">
      {/* Header */}
      <div className="flex items-center" data-id="element-11248">
        <button onClick={() => navigate('/suppliers')} className="flex items-center text-gray-900 hover:text-gray-600 transition-colors text-lg font-semibold" data-id="element-11249">
          <ArrowLeftIcon size={20} className="mr-2" data-id="element-11250" />
          All suppliers
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-11251">
        {/* Left Column - Main Details */}
        <div className="lg:col-span-2 space-y-6" data-id="element-11252">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-11253">
            {/* Top Section */}
            <div className="p-8 border-b border-gray-100" data-id="element-11254">
              <div className="flex justify-between items-start" data-id="element-11255">
                <div data-id="element-11256">
                  <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-11257">
                    {supplier.name}
                  </h1>
                  <div className="flex items-center text-sm" data-id="element-11258">
                    <span className="text-gray-500 mr-2" data-id="element-11259">You owe them:</span>
                    <span className="px-2.5 py-0.5 bg-[#F7941D] text-white text-xs font-semibold rounded-md shadow-sm" data-id="element-11260">
                      {supplier.amountOwed}
                    </span>
                  </div>
                </div>
                <div className="flex items-center space-x-3" data-id="element-11261">
                  <button onClick={() => navigate(`/bill-form?supplierId=${supplier.id}`)} className="px-4 py-2 bg-[#F7941D] hover:bg-[#D97B06] text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-11262">
                    Add bill
                  </button>
                  <button className="p-2 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors" data-id="element-11263">
                    <MoreHorizontalIcon size={20} data-id="element-11264" />
                  </button>
                </div>
              </div>
            </div>

            {/* Details Section */}
            <div className="p-8 border-b border-gray-100" data-id="element-11265">
              <div className="flex justify-between items-center mb-6" data-id="element-11266">
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider" data-id="element-11267">
                  Details
                </h3>
                <button onClick={() => navigate(`/supplier-form?id=${supplier.id}`)} className="text-sm font-medium text-[#F7941D] hover:text-[#D97B06]" data-id="element-11268">
                  Edit
                </button>
              </div>
              <div className="space-y-4" data-id="element-11269">
                {/* Type */}
                <div className="grid grid-cols-3 gap-4 border-b border-gray-50 pb-4" data-id="element-11270">
                  <div className="text-sm text-gray-500" data-id="element-11271">Type</div>
                  <div className="col-span-2" data-id="element-11272">
                    {supplier.supplierType === 'company' ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100" data-id="element-11273">
                        <BuildingIcon size={12} className="mr-1.5" data-id="element-11274" />
                        Company
                      </span> : <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-purple-50 text-purple-700 border border-purple-100" data-id="element-11275">
                        <UserIcon size={12} className="mr-1.5" data-id="element-11276" />
                        Individual
                      </span>}
                  </div>
                </div>

                {/* Company-specific fields */}
                {supplier.supplierType === 'company' && <>
                    <div className="grid grid-cols-3 gap-4" data-id="element-11277">
                      <div className="text-sm text-gray-500" data-id="element-11278">
                        Company name (Local)
                      </div>
                      <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-11279">
                        {supplier.companyNameTh || '—'}
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4" data-id="element-11280">
                      <div className="text-sm text-gray-500" data-id="element-11281">
                        Company name (English)
                      </div>
                      <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-11282">
                        {supplier.companyNameEn || '—'}
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4" data-id="element-11283">
                      <div className="text-sm text-gray-500" data-id="element-11284">
                        Tax ID / Registration no.
                      </div>
                      <div className="col-span-2 text-sm font-medium text-gray-900 font-mono" data-id="element-11285">
                        {supplier.taxId || '—'}
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4" data-id="element-11286">
                      <div className="text-sm text-gray-500" data-id="element-11287">Branch</div>
                      <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-11288">
                        {supplier.branch === '00000' ? '00000 (Head Office)' : supplier.branch || '—'}
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4" data-id="element-11289">
                      <div className="text-sm text-gray-500" data-id="element-11290">
                        VAT registered
                      </div>
                      <div className="col-span-2" data-id="element-11291">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${supplier.vatRegistered ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-600'}`} data-id="element-11292">
                          {supplier.vatRegistered ? 'Yes' : 'No'}
                        </span>
                      </div>
                    </div>
                    {supplier.contactPerson && <div className="grid grid-cols-3 gap-4" data-id="element-11293">
                        <div className="text-sm text-gray-500" data-id="element-11294">
                          Contact person
                        </div>
                        <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-11295">
                          {supplier.contactPerson}
                        </div>
                      </div>}
                  </>}

                {/* Individual-specific fields */}
                {supplier.supplierType === 'individual' && <>
                    <div className="grid grid-cols-3 gap-4" data-id="element-11296">
                      <div className="text-sm text-gray-500" data-id="element-11297">
                        Full name (Thai)
                      </div>
                      <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-11298">
                        {supplier.fullNameTh || '—'}
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4" data-id="element-11299">
                      <div className="text-sm text-gray-500" data-id="element-11300">
                        Full name (English)
                      </div>
                      <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-11301">
                        {supplier.fullNameEn || '—'}
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4" data-id="element-11302">
                      <div className="text-sm text-gray-500" data-id="element-11303">ID type</div>
                      <div className="col-span-2 text-sm font-medium text-gray-900 capitalize" data-id="element-11304">
                        {supplier.idType === 'citizen_id' ? 'Citizen ID' : supplier.idType === 'passport' ? 'Passport' : '—'}
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4" data-id="element-11305">
                      <div className="text-sm text-gray-500" data-id="element-11306">ID number</div>
                      <div className="col-span-2 text-sm font-medium text-gray-900 font-mono" data-id="element-11307">
                        {supplier.idNumber || '—'}
                      </div>
                    </div>
                    {supplier.individualTaxId && <div className="grid grid-cols-3 gap-4" data-id="element-11308">
                        <div className="text-sm text-gray-500" data-id="element-11309">Tax ID</div>
                        <div className="col-span-2 text-sm font-medium text-gray-900 font-mono" data-id="element-11310">
                          {supplier.individualTaxId}
                        </div>
                      </div>}
                  </>}

                {/* Shared fields */}
                <div className="grid grid-cols-3 gap-4 border-t border-gray-50 pt-4" data-id="element-11311">
                  <div className="text-sm text-gray-500" data-id="element-11312">Email</div>
                  <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-11313">
                    {supplier.email}
                  </div>
                </div>
                {supplier.phone && <div className="grid grid-cols-3 gap-4" data-id="element-11314">
                    <div className="text-sm text-gray-500" data-id="element-11315">Phone</div>
                    <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-11316">
                      {supplier.phone}
                    </div>
                  </div>}
                <div className="grid grid-cols-3 gap-4" data-id="element-11317">
                  <div className="text-sm text-gray-500" data-id="element-11318">Billing email</div>
                  <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-11319">
                    {billingEmail}
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4" data-id="element-11320">
                  <div className="text-sm text-gray-500" data-id="element-11321">Address</div>
                  <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-11322">
                    {fullAddress || '—'}
                  </div>
                </div>
                {supplier.internalReference && <div className="grid grid-cols-3 gap-4" data-id="element-11323">
                    <div className="text-sm text-gray-500" data-id="element-11324">
                      Internal reference
                    </div>
                    <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-11325">
                      {supplier.internalReference}
                    </div>
                  </div>}
              </div>
            </div>

            {/* Pay-in Method Section */}
            <div className="p-8 border-b border-gray-100" data-id="element-11326">
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-6" data-id="element-11327">
                Pay-in method
              </h3>
              <div className="bg-gray-50 rounded-lg p-5 border border-gray-200" data-id="element-11328">
                <div className="flex items-center" data-id="element-11329">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-4 ${supplier.payInMethod === 'acquiring' ? 'bg-green-100' : 'bg-blue-100'}`} data-id="element-11330">
                    {supplier.payInMethod === 'acquiring' ? <WalletIcon size={20} className="text-green-700" data-id="element-11331" /> : <CreditCardIcon size={20} className="text-blue-700" data-id="element-11332" />}
                  </div>
                  <div data-id="element-11333">
                    <div className="text-sm font-semibold text-gray-900" data-id="element-11334">
                      {supplier.payInMethod === 'acquiring' ? 'Acquiring Volume' : 'Card Payment'}
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-11335">
                      {supplier.payInMethod === 'acquiring' ? 'Funded from merchant acquiring settlement balance' : 'Charged to linked card'}
                    </div>
                  </div>
                </div>
                {supplier.payInMethod === 'card' && supplier.cardNumber && <div className="grid grid-cols-2 gap-y-3 gap-x-6 mt-4 pt-4 border-t border-gray-200" data-id="element-11336">
                    <div data-id="element-11337">
                      <div className="text-xs text-gray-500 mb-1" data-id="element-11338">
                        Cardholder
                      </div>
                      <div className="text-sm font-medium text-gray-900" data-id="element-11339">
                        {supplier.cardholderName}
                      </div>
                    </div>
                    <div data-id="element-11340">
                      <div className="text-xs text-gray-500 mb-1" data-id="element-11341">
                        Card number
                      </div>
                      <div className="text-sm font-medium text-gray-900 font-mono" data-id="element-11342">
                        {supplier.cardNumber}
                      </div>
                    </div>
                  </div>}
              </div>
            </div>

            {/* Payout Method Section */}
            <div className="p-8 border-b border-gray-100" data-id="element-11343">
              <div className="flex justify-between items-center mb-6" data-id="element-11344">
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider" data-id="element-11345">
                  Payout method
                </h3>
                <button onClick={() => navigate(`/supplier-form?id=${supplier.id}`)} className="text-sm font-medium text-[#F7941D] hover:text-[#D97B06]" data-id="element-11346">
                  Edit payout method
                </button>
              </div>

              <div className="bg-gray-50 rounded-lg p-5 border border-gray-200" data-id="element-11347">
                {supplier.payoutMethod === 'bank_account' ? <>
                    <div className="flex items-center mb-4" data-id="element-11348">
                      <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center mr-4" data-id="element-11349">
                        <LandmarkIcon size={20} className="text-green-700" data-id="element-11350" />
                      </div>
                      <div data-id="element-11351">
                        <div className="text-sm font-semibold text-gray-900" data-id="element-11352">
                          Bank Account
                        </div>
                        <div className="text-xs text-gray-500" data-id="element-11353">
                          Direct transfer
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-y-3 gap-x-6 mt-4 pt-4 border-t border-gray-200" data-id="element-11354">
                      <div data-id="element-11355">
                        <div className="text-xs text-gray-500 mb-1" data-id="element-11356">
                          Bank name
                        </div>
                        <div className="text-sm font-medium text-gray-900" data-id="element-11357">
                          {supplier.bankName}
                        </div>
                      </div>
                      <div data-id="element-11358">
                        <div className="text-xs text-gray-500 mb-1" data-id="element-11359">
                          Account number
                        </div>
                        <div className="text-sm font-medium text-gray-900 font-mono" data-id="element-11360">
                          {'***' + supplier.accountNumber.slice(-4)}
                        </div>
                      </div>
                      <div data-id="element-11361">
                        <div className="text-xs text-gray-500 mb-1" data-id="element-11362">
                          Account name
                        </div>
                        <div className="text-sm font-medium text-gray-900" data-id="element-11363">
                          {supplier.accountName}
                        </div>
                      </div>
                    </div>
                  </> : <>
                    <div className="flex items-center mb-4" data-id="element-11364">
                      <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center mr-4" data-id="element-11365">
                        <SmartphoneIcon size={20} className="text-purple-700" data-id="element-11366" />
                      </div>
                      <div data-id="element-11367">
                        <div className="text-sm font-semibold text-gray-900" data-id="element-11368">
                          PromptPay
                        </div>
                        <div className="text-xs text-gray-500" data-id="element-11369">
                          {supplier.promptPayType === 'phone' ? 'Phone number' : supplier.promptPayType === 'citizen_id' ? 'Citizen ID' : 'Tax ID'}
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-y-3 gap-x-6 mt-4 pt-4 border-t border-gray-200" data-id="element-11370">
                      <div data-id="element-11371">
                        <div className="text-xs text-gray-500 mb-1" data-id="element-11372">
                          PromptPay ID
                        </div>
                        <div className="text-sm font-medium text-gray-900 font-mono" data-id="element-11373">
                          {'***' + supplier.promptPayId.slice(-4)}
                        </div>
                      </div>
                      <div data-id="element-11374">
                        <div className="text-xs text-gray-500 mb-1" data-id="element-11375">
                          Display name
                        </div>
                        <div className="text-sm font-medium text-gray-900" data-id="element-11376">
                          {supplier.promptPayName}
                        </div>
                      </div>
                    </div>
                  </>}
              </div>
            </div>

            {/* Source Section */}
            <div className="p-8 border-b border-gray-100" data-id="element-11377">
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-6" data-id="element-11378">
                Source
              </h3>
              <div className="space-y-4" data-id="element-11379">
                <div className="grid grid-cols-3 gap-4" data-id="element-11380">
                  <div className="text-sm text-gray-500" data-id="element-11381">Platform</div>
                  <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-11382">
                    {supplier.source}
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4" data-id="element-11383">
                  <div className="text-sm text-gray-500" data-id="element-11384">Date created</div>
                  <div className="col-span-2 text-sm font-medium text-gray-900" data-id="element-11385">
                    {supplier.dateCreated}
                  </div>
                </div>
              </div>
            </div>

            {/* Bills Section */}
            <div className="p-8" data-id="element-11386">
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4" data-id="element-11387">
                Bills
              </h3>
              <div className="border border-gray-200 rounded-lg p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors group" data-id="element-11388">
                <div data-id="element-11389">
                  <div className="text-sm font-medium text-gray-900 mb-1" data-id="element-11390">
                    {bills.total} bill{bills.total !== 1 ? 's' : ''} for{' '}
                    {supplier.name}
                  </div>
                  <div className="text-sm text-gray-500" data-id="element-11391">
                    {bills.due} due, {bills.awaitingApproval} awaiting approval,{' '}
                    {bills.scheduled} scheduled and {bills.paid} paid.
                  </div>
                </div>
                <ChevronRightIcon size={20} className="text-gray-400 group-hover:text-gray-600 transition-colors" data-id="element-11392" />
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - History */}
        <div className="lg:col-span-1" data-id="element-11393">
          <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-4" data-id="element-11394">
            History
          </h3>
          <div className="space-y-6" data-id="element-11395">
            {history.map((event, index) => <div key={event.id} className="relative flex gap-4" data-id="element-11396">
                {index !== history.length - 1 && <div className="absolute left-[9px] top-6 bottom-[-24px] w-px bg-gray-200" data-id="element-11397"></div>}
                <div className="relative z-10 flex-shrink-0 w-5 h-5 bg-gray-100 rounded flex items-center justify-center mt-0.5" data-id="element-11398">
                  <PlusIcon size={12} className="text-gray-600" data-id="element-11399" />
                </div>
                <div data-id="element-11400">
                  <p className="text-sm font-medium text-gray-900" data-id="element-11401">
                    {event.action}
                  </p>
                  <p className="text-sm text-gray-500 mt-1" data-id="element-11402">{event.date}</p>
                </div>
              </div>)}
          </div>
        </div>
      </div>
    </div>;
}